import type { DoctorsListOrder } from "@hermes/api";
import { Flag } from "@hermes/flags";
import { useMessage, useNow } from "@hermes/intl";
import { searchParamTypes as t, useSearchParams } from "@hermes/shared";
import {
  colors,
  createStaticStyles,
  cssFns,
  FilterButton,
  FilterOutlinedButton,
  FilterScrollButton,
  FilterSearchButton,
  type ModalState,
  useScreenDetector,
  useStaticStyles,
} from "@hermes/ui";
import { match, Temporal } from "@hermes/utils";
import {
  Children,
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { useAppConfig } from "#internal/app/config/context";

import { ChildrenAgeFilter } from "./children-age";
import { DateFilter } from "./date";
import { InsurancesFilter } from "./insurances";
import { LanguageFilter } from "./language";
import { SortDesktop } from "./sort";
import type { DoctorsListParams } from "./types";

export const useParams = () => {
  const [params, setParams] = useSearchParams(
    {
      sort: t.string<DoctorsListOrder | undefined>({
        enum: ["rating", "price", "experience"],
      }),
      nearme: t.boolean(),
      date: t.plainDate(),
      language: t.string({}),
      insurance: t.string({}),
      child_age: t.number<number>(),
    },
    { allowUnknown: true },
  );

  return [
    {
      nearme: params.nearme,
      sort: params.nearme
        ? "distance"
        : (params.sort as DoctorsListOrder | undefined),
      date: params.date,
      language: params.language,
      insurance: params.insurance,
      child_age: params.child_age,
    },
    setParams,
  ] as const;
};

const styles = {
  base: {
    container: createStaticStyles({
      mobile: { backgroundColor: colors.principal.ebblue },
      desktop: { display: "contents" },
    }),
    childAgeContainer: createStaticStyles({
      mobile: [
        cssFns.paddingInline(10),
        { backgroundColor: colors.principal.ebblue, paddingBlockEnd: 10 },
      ],
      desktop: { paddingBlockStart: 16 },
    }),
  },
  scrollableRow: {
    basicContainer: createStaticStyles({
      common: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 8,
        position: "relative",
      },
      mobile: cssFns.padding(10),
    }),
    filters: createStaticStyles({
      common: {
        display: "flex",
        alignItems: "center",
        overflowX: "scroll",
        scrollbarWidth: "none",
        scrollBehavior: "smooth",
        WebkitOverflowScrolling: "touch",
        width: "100%",
      },
      mobile: {
        columnGap: 6,
        paddingInlineEnd: 0,
      },
      desktop: {
        columnGap: 16,
      },
    }),
  },
};

export type DoctorListFiltersProps = {
  params: DoctorsListParams;
  onParamsChange: (params: DoctorsListParams) => void;
  enableChildFilter?: boolean;
  isChild?: boolean;
  onChildChange?: () => void;
  variant?: "filter-search" | "filter-outlined";
  filtersModalState?: ModalState;
  isAnyParamSet: boolean;
};

export const DoctorListFilters = ({
  params,
  onParamsChange,
  enableChildFilter,
  isChild,
  onChildChange,
  variant: variantOverride,
  filtersModalState,
  isAnyParamSet,
}: DoctorListFiltersProps) => {
  const { isMobile } = useScreenDetector();
  const message = useMessage();
  const { insurances } = useAppConfig();
  const now = useNow("day");
  const { staticStyles } = useStaticStyles();

  const variant = variantOverride
    ? variantOverride
    : isMobile
      ? "filter-search"
      : "filter-outlined";
  const ButtonComponent = match(variant, {
    "filter-outlined": () => FilterOutlinedButton,
    "filter-search": () => FilterSearchButton,
  });

  const isTodayFilterActive = params.date
    ? Temporal.PlainDate.compare(params.date, now.toPlainDate()) === 0
    : false;

  const filters = [
    {
      key: "child",
      element: enableChildFilter ? (
        <Flag
          id="1a7d4b25-6711-4f32-8118-ba3a5556d167"
          description="Enable children filter in doctors list page"
        >
          <ButtonComponent
            text={message({
              id: "doctors-list.filters.children",
              default: "Детский",
            })}
            active={isChild}
            onPress={onChildChange}
          />
        </Flag>
      ) : null,
      isActive: isChild,
    },
    {
      key: "nearme",
      element: (
        <Flag
          id="635e3e5e-50c6-47f6-b2d3-fdf24547c652"
          description="Enable near filter in doctors list page"
        >
          <ButtonComponent
            text={message({
              id: "doctors-list.filters.near",
              default: "Рядом",
            })}
            active={params.nearme}
            onPress={() =>
              onParamsChange({
                nearme: !params.nearme,
                sort: undefined,
              })
            }
          />
        </Flag>
      ),
      isActive: params.nearme,
    },
    {
      key: "date",
      element: (
        <Flag
          id="ff5b8251-73f3-43b0-91c6-205bd5d4b3ba"
          description="Enable date filter in doctors list page"
        >
          <ButtonComponent
            text={message({
              id: "doctors-list.filters.for-today",
              default: "For today",
            })}
            active={isTodayFilterActive}
            onPress={() => {
              onParamsChange({
                date: isTodayFilterActive ? undefined : now.toPlainDate(),
              });
            }}
          />
          <DateFilter
            params={params}
            onParamsChange={onParamsChange}
            variant={variant}
          />
        </Flag>
      ),
      isActive: Boolean(params.date),
    },
    {
      key: "language",
      element: (
        <Flag id="language-filter">
          <LanguageFilter
            params={params}
            onParamsChange={onParamsChange}
            variant={variant}
          />
        </Flag>
      ),
      isActive: Boolean(params.language),
    },
    {
      key: "insurance",
      element:
        insurances.length > 0 ? (
          <InsurancesFilter
            params={params}
            onParamsChange={onParamsChange}
            variant={variant}
          />
        ) : null,
      isActive: Boolean(params.insurance),
    },
  ];

  const sortedFilters = filters
    .filter((f) => f.element !== null)
    .sort((a, b) => Number(b.isActive) - Number(a.isActive));

  return (
    <div css={staticStyles(styles.base.container)}>
      <ScrollableFiltersRow>
        {isMobile ? (
          <FilterButton.Filled
            onPress={filtersModalState?.open}
            badge={isAnyParamSet}
          />
        ) : (
          <SortDesktop params={params} onParamsChange={onParamsChange} />
        )}
        {sortedFilters.map(({ key, element }) =>
          element ? (
            <div key={key} style={{ display: "contents" }}>
              {element}
            </div>
          ) : null,
        )}
      </ScrollableFiltersRow>
      {isChild && (
        <div css={staticStyles(styles.base.childAgeContainer)}>
          <ChildrenAgeFilter
            params={params}
            onParamsChange={onParamsChange}
            variant={variant}
          />
        </div>
      )}
    </div>
  );
};

const ScrollableFiltersRow = memo(
  ({ children }: { children: React.ReactNode }) => {
    const { isMobile } = useScreenDetector();

    const scrollRef = useRef<HTMLDivElement>(null);

    const { staticStyles } = useStaticStyles();

    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const scrollByAmount = (direction: "left" | "right") => {
      requestAnimationFrame(() => {
        const container = scrollRef.current;
        if (!container) return;

        const amount = container.clientWidth - 120;
        container.scrollBy({
          left: direction === "left" ? -amount : amount,
          behavior: "smooth",
        });
      });
    };

    const updateScrollButtonsVisibility = useCallback(() => {
      const container = scrollRef.current;
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;
      const nextCanScrollLeft = scrollLeft > 0;
      const maxScrollLeft = scrollWidth - clientWidth;
      const nextCanScrollRight = scrollLeft < maxScrollLeft - 1;

      setCanScrollLeft((prev) =>
        prev !== nextCanScrollLeft ? nextCanScrollLeft : prev,
      );
      setCanScrollRight((prev) =>
        prev !== nextCanScrollRight ? nextCanScrollRight : prev,
      );
    }, []);

    const childrenCount = Children.count(children);

    useLayoutEffect(() => {
      const container = scrollRef.current;
      if (!container) return;

      updateScrollButtonsVisibility();

      container.addEventListener("scroll", updateScrollButtonsVisibility);
      window.addEventListener("resize", updateScrollButtonsVisibility);

      return () => {
        container.removeEventListener("scroll", updateScrollButtonsVisibility);
        window.removeEventListener("resize", updateScrollButtonsVisibility);
      };
    }, [updateScrollButtonsVisibility, childrenCount]);
    return (
      <div css={staticStyles(styles.scrollableRow.basicContainer)}>
        {isMobile && (
          <MobileScrollButtons
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            scrollByAmount={scrollByAmount}
          />
        )}
        <div ref={scrollRef} css={staticStyles(styles.scrollableRow.filters)}>
          {children}
        </div>
      </div>
    );
  },
);

const MobileScrollButtons = memo(
  ({
    canScrollLeft,
    canScrollRight,
    scrollByAmount,
  }: {
    canScrollLeft: boolean;
    canScrollRight: boolean;
    scrollByAmount: (dir: "left" | "right") => void;
  }) => (
    <>
      <FilterScrollButton.Left
        text="prev"
        disabled={!canScrollLeft}
        onPress={() => scrollByAmount("left")}
        aria-hidden={!canScrollLeft}
      />
      <FilterScrollButton.Right
        text="next"
        disabled={!canScrollRight}
        onPress={() => scrollByAmount("right")}
        aria-hidden={!canScrollRight}
      />
    </>
  ),
);
